import { deriveContext } from './businessTaxonomy';

const BUSINESS_CONTEXT_KEY = 'wowrolla_business_context_v1';
const LEGACY_SELECTED_STACK_KEY = 'wowrolla:selected_stack';

const safeStorage = (persistent = false) => {
  if (typeof window === 'undefined') return null;
  try {
    return persistent ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

const parseJson = (raw, fallback = {}) => {
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

export const loadBusinessContext = (isAuthenticated = false) => {
  const storage = safeStorage(Boolean(isAuthenticated));
  const legacyStorage = safeStorage(false);
  const persisted = parseJson(storage?.getItem(BUSINESS_CONTEXT_KEY), {});
  const legacySelectedStackId = normalizeText(storage?.getItem(LEGACY_SELECTED_STACK_KEY) || legacyStorage?.getItem(LEGACY_SELECTED_STACK_KEY));

  // Rehydrate business context from the canonical businessType field while preserving older industry payloads.
  const normalized = deriveContext({
    businessType: persisted.businessType || persisted.industry,
    selectedStackId: persisted.selectedStackId || legacySelectedStackId,
  });

  return {
    businessType: normalizeText(normalized.businessType),
    selectedStackId: normalizeText(normalized.selectedStackId),
    currentToolStack: normalizeText(persisted.currentToolStack),
    selectedWorkflows: Array.isArray(persisted.selectedWorkflows) ? persisted.selectedWorkflows.filter(Boolean) : [],
    teamSize: persisted.teamSize || '',
    sourceScreen: normalizeText(persisted.sourceScreen),
    updatedAt: normalizeText(persisted.updatedAt),
  };
};

export const saveBusinessContext = (partial, isAuthenticated = false) => {
  const storage = safeStorage(Boolean(isAuthenticated));
  if (!storage) return loadBusinessContext(isAuthenticated);

  const current = loadBusinessContext(isAuthenticated);
  const merged = {
    ...current,
    ...(partial || {}),
    selectedWorkflows: Array.isArray(partial?.selectedWorkflows)
      ? partial.selectedWorkflows.filter(Boolean)
      : current.selectedWorkflows,
    updatedAt: new Date().toISOString(),
  };

  const normalized = deriveContext({
    businessType: merged.businessType,
    selectedStackId: merged.selectedStackId,
  });

  const next = {
    ...merged,
    ...normalized,
  };

  storage.setItem(BUSINESS_CONTEXT_KEY, JSON.stringify(next));
  if (next.selectedStackId) {
    storage.setItem(LEGACY_SELECTED_STACK_KEY, next.selectedStackId);
  }
  window.dispatchEvent(new CustomEvent('wowrolla:business-context-updated', { detail: next }));
  return next;
};

export const clearBusinessContext = (isAuthenticated = false) => {
  const storage = safeStorage(Boolean(isAuthenticated));
  storage?.removeItem(BUSINESS_CONTEXT_KEY);
  storage?.removeItem(LEGACY_SELECTED_STACK_KEY);
};

export const migrateGuestBusinessContextToAuthenticated = () => {
  const guest = loadBusinessContext(false);
  const hasGuestContext = Object.values(guest).some((value) => (Array.isArray(value) ? value.length > 0 : Boolean(value)));
  if (!hasGuestContext) return null;
  const migrated = saveBusinessContext(guest, true);
  clearBusinessContext(false);
  return migrated;
};

export const buildBusinessContextNotice = ({ previousBusinessType = '', nextBusinessType = '', nextStackLabel = '' } = {}) => {
  if (!nextBusinessType && !nextStackLabel) return '';
  const targetLabel = nextStackLabel || nextBusinessType;
  if (previousBusinessType && previousBusinessType !== nextBusinessType) {
    return `Updated your active business profile from ${previousBusinessType} to ${targetLabel}. Finder, AI Stacks, Readiness Studio, and My AI Flow will now stay aligned.`;
  }
  return `Using ${targetLabel} as your active business profile across Wowrolla so recommendations stay consistent.`;
};